import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import * as OTPAuth from 'otpauth';
import * as QRCode from 'qrcode';
import { useAuth } from '../contexts/AuthContext';
import { useFlight } from '../contexts/FlightContext';
import { XCircleIcon, CheckIcon, EyeIcon, EyeSlashIcon, ArrowLeftIcon, KeyIcon, BellIcon, BellSlashIcon, SpinnerIcon } from './Icons';
import { subscribeUser, unsubscribeUser, getSubscription, isPushSupported } from '../services/pushNotificationService';

interface ProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose }) => {
    const { user, changePassword, setPin, enableOtp, disableOtp, vapidKey, updateUserPushSubscription } = useAuth();
    const { showNotification } = useFlight();
    const [activeSection, setActiveSection] = useState<'main' | 'changePassword' | 'setupPin' | 'setupOtp'>('main');

    // State for password change
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    // State for PIN setup
    const [authPassword, setAuthPassword] = useState('');
    const [newPin, setNewPin] = useState('');
    const [confirmNewPin, setConfirmNewPin] = useState('');
    const [isSettingPin, setIsSettingPin] = useState(false);

    // State for OTP setup
    const [otpSetupData, setOtpSetupData] = useState<{ secret: string; qrCodeDataUrl: string } | null>(null);
    const [otpPassword, setOtpPassword] = useState('');
    const [otpToken, setOtpToken] = useState('');
    const [isSettingOtp, setIsSettingOtp] = useState(false);
    
    // State for Push Notifications
    const [isPushEnabled, setIsPushEnabled] = useState(false);
    const [isPushLoading, setIsPushLoading] = useState(true);
    const [pushPermissionStatus, setPushPermissionStatus] = useState<NotificationPermission | null>(null);
    
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const clearMessages = () => {
        setError('');
        setSuccess('');
    }

    useEffect(() => {
        if (!isOpen) return;
        
        // Check Push Notification status
        if (isPushSupported()) {
            setPushPermissionStatus(Notification.permission);
            setIsPushLoading(true);
            getSubscription().then(subscription => {
                setIsPushEnabled(!!subscription);
                setIsPushLoading(false);
            });
        } else {
            setIsPushLoading(false);
        }
        
        // Generate OTP QR code if needed
        if (activeSection === 'setupOtp' && !user?.otpEnabled && !otpSetupData) {
            const generateQrCode = async () => {
                try {
                    const totp = new OTPAuth.TOTP({
                        issuer: 'JT-Crew Movement',
                        label: user?.username,
                        algorithm: 'SHA1',
                        digits: 6,
                        period: 30,
                    });
                    const uri = totp.toString();
                    const secret = totp.secret.base32;
                    const qrCodeDataUrl = await QRCode.toDataURL(uri, { errorCorrectionLevel: 'M' });
                    setOtpSetupData({ secret, qrCodeDataUrl });
                } catch(e) {
                    setError('Failed to initialize OTP generator or QR code.');
                    console.error(e);
                }
            };
            generateQrCode();
        }
    }, [isOpen, activeSection, user, otpSetupData]);

    const handleTogglePush = async () => {
        clearMessages();
        setIsPushLoading(true);

        if (isPushEnabled) { // Try to unsubscribe
            try {
                const unsubscribed = await unsubscribeUser();
                if (unsubscribed) {
                    await updateUserPushSubscription(null);
                    setIsPushEnabled(false);
                    setSuccess("Push notifications disabled.");
                } else {
                    setError("Failed to disable push notifications.");
                }
            } catch (e) {
                setError("An error occurred while disabling notifications.");
                console.error(e);
            }
        } else { // Try to subscribe
            if (!vapidKey) {
                setError("VAPID key not configured by admin. Cannot enable push notifications.");
                setIsPushLoading(false);
                return;
            }
            try {
                const subscription = await subscribeUser(vapidKey);
                await updateUserPushSubscription(subscription.toJSON());
                setIsPushEnabled(true);
                setSuccess("Push notifications enabled successfully!");
            } catch (e) {
                const errorMessage = e instanceof Error ? e.message : "An unknown error occurred.";
                setError(`Failed to subscribe: ${errorMessage}`);
                console.error("Push subscription error:", e);
            }
        }
        setIsPushLoading(false);
    };


    const handlePasswordChange = async () => {
        clearMessages();
        if (newPassword !== confirmNewPassword) {
            setError("New passwords do not match.");
            return;
        }
        setIsChangingPassword(true);
        const result = await changePassword(currentPassword, newPassword);
        if (result.success) {
            setSuccess(result.message);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
            setTimeout(() => {
                clearMessages();
                setActiveSection('main');
            }, 2000);
        } else {
            setError(result.message);
        }
        setIsChangingPassword(false);
    };

    const handlePinSetup = async () => {
        clearMessages();
        if(newPin !== confirmNewPin) {
            setError("PINs do not match.");
            return;
        }
        setIsSettingPin(true);
        const result = await setPin(authPassword, newPin);
        if (result.success) {
            setSuccess(result.message);
            setAuthPassword('');
            setNewPin('');
            setConfirmNewPin('');
            setTimeout(() => {
                clearMessages();
                setActiveSection('main');
            }, 2000);
        } else {
            setError(result.message);
        }
        setIsSettingPin(false);
    }
    
    const handleRemovePin = async () => {
        if(window.confirm("Are you sure you want to remove your Security PIN?")) {
            clearMessages();
            // We need password confirmation to remove PIN as well.
            const password = prompt("Please enter your password to confirm.");
            if (password) {
                const result = await setPin(password, null);
                if (result.success) {
                    showNotification(result.message, 'success');
                } else {
                    showNotification(result.message, 'error');
                }
            }
        }
    }

    const handleEnableOtp = async () => {
        if (!otpSetupData) return;
        clearMessages();
        setIsSettingOtp(true);
        const result = await enableOtp(otpPassword, otpSetupData.secret, otpToken);
        if (result.success) {
            setSuccess(result.message);
            setOtpPassword('');
            setOtpToken('');
            setTimeout(() => {
                clearMessages();
                setActiveSection('main');
            }, 2000);
        } else {
            setError(result.message);
        }
        setIsSettingOtp(false);
    };

    const handleDisableOtp = async () => {
        clearMessages();
        if (!otpPassword) {
            setError("Please enter your password to disable OTP.");
            return;
        }
        setIsSettingOtp(true);
        const result = await disableOtp(otpPassword);
        if (result.success) {
            setSuccess(result.message);
            setOtpPassword('');
            setTimeout(() => {
                clearMessages();
                setActiveSection('main');
            }, 2000);
        } else {
            setError(result.message);
        }
        setIsSettingOtp(false);
    };

    const resetState = useCallback(() => {
        setActiveSection('main');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setAuthPassword('');
        setNewPin('');
        setConfirmNewPin('');
        setOtpPassword('');
        setOtpToken('');
        setOtpSetupData(null);
        clearMessages();
    }, []);

    const handleClose = () => {
        resetState();
        onClose();
    };

    const renderMain = () => (
        <>
            <h2 className="text-2xl font-bold text-white mb-2">My Profile</h2>
            <div className="text-center text-slate-300 mb-6">
                <p className="text-xl">{user?.username}</p>
                <p className="text-sm bg-blue-900/50 text-blue-300 inline-block px-3 py-1 rounded-full mt-1">{user?.role}</p>
            </div>
            <div className="space-y-4">
                <button onClick={() => setActiveSection('changePassword')} className="w-full text-left bg-slate-700/50 hover:bg-slate-700 p-4 rounded-lg transition-colors">
                    <h3 className="font-semibold text-white">Change Password</h3>
                    <p className="text-sm text-slate-400">Update your account password.</p>
                </button>
                <button onClick={() => setActiveSection('setupPin')} className="w-full text-left bg-slate-700/50 hover:bg-slate-700 p-4 rounded-lg transition-colors">
                    <h3 className="font-semibold text-white">Security PIN</h3>
                    {user?.pin 
                        ? <p className="text-sm text-green-400">Security PIN is enabled.</p>
                        : <p className="text-sm text-slate-400">Set up a 4-digit PIN for quick login.</p>
                    }
                </button>
                <button onClick={() => setActiveSection('setupOtp')} className="w-full text-left bg-slate-700/50 hover:bg-slate-700 p-4 rounded-lg transition-colors">
                    <h3 className="font-semibold text-white">Authenticator App (OTP)</h3>
                    {user?.otpEnabled 
                        ? <p className="text-sm text-green-400">Authenticator app is enabled.</p>
                        : <p className="text-sm text-slate-400">Set up an authenticator for highest security.</p>
                    }
                </button>

                 { isPushSupported() && (
                    <div className="w-full text-left bg-slate-700/50 p-4 rounded-lg">
                        <div className="flex justify-between items-center">
                            <div>
                                <h3 className="font-semibold text-white">Push Notifications</h3>
                                <p className={`text-sm ${isPushEnabled ? 'text-green-400' : 'text-slate-400'}`}>
                                    {isPushLoading ? 'Checking status...' : (isPushEnabled ? 'Enabled' : 'Disabled')}
                                </p>
                            </div>
                            {pushPermissionStatus !== 'denied' && (
                                <button
                                    onClick={handleTogglePush}
                                    disabled={isPushLoading}
                                    className={`font-bold py-2 px-4 rounded-lg text-sm transition-colors disabled:bg-slate-500 disabled:cursor-wait w-24 flex justify-center items-center ${
                                        isPushEnabled
                                        ? 'bg-red-600 hover:bg-red-700 text-white'
                                        : 'bg-green-600 hover:bg-green-700 text-white'
                                    }`}
                                >
                                    {isPushLoading ? (
                                        <SpinnerIcon className="w-5 h-5 animate-spin-slow" />
                                    ) : isPushEnabled ? (
                                        'Disable'
                                    ) : (
                                        'Enable'
                                    )}
                                </button>
                            )}
                        </div>
                        {pushPermissionStatus === 'denied' && (
                            <p className="text-xs text-amber-400 mt-2 pt-2 border-t border-slate-600/50">You have blocked notifications. Please enable them in your browser/system settings to use this feature.</p>
                        )}
                    </div>
                )}

                {user?.pin && (
                    <button onClick={handleRemovePin} className="w-full text-center text-sm text-red-400 hover:text-red-300 py-2">
                        Remove Security PIN
                    </button>
                )}
            </div>
        </>
    );

    const renderChangePassword = () => (
        <>
            <button onClick={() => { clearMessages(); setActiveSection('main'); }} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-4"><ArrowLeftIcon className="w-4 h-4" /> Back to Profile</button>
            <h2 className="text-2xl font-bold text-white mb-4">Change Password</h2>
            <div className="space-y-4">
                <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Current Password" className="w-full" />
                <div className="relative">
                    <input type={isNewPasswordVisible ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New Password (min 6 chars)" className="w-full" />
                     <button onClick={() => setIsNewPasswordVisible(p => !p)} className="absolute inset-y-0 right-0 px-3 text-slate-400">{isNewPasswordVisible ? <EyeSlashIcon className="w-5 h-5"/> : <EyeIcon className="w-5 h-5"/>}</button>
                </div>
                <input type="password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} placeholder="Confirm New Password" className="w-full" />
            </div>
            <div className="mt-6 flex justify-end">
                <button onClick={handlePasswordChange} disabled={isChangingPassword} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg disabled:bg-slate-500">{isChangingPassword ? 'Updating...' : 'Update Password'}</button>
            </div>
        </>
    );
    
    const renderSetupPin = () => (
        <>
            <button onClick={() => { clearMessages(); setActiveSection('main'); }} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-4"><ArrowLeftIcon className="w-4 h-4" /> Back to Profile</button>
            <h2 className="text-2xl font-bold text-white mb-4">{user?.pin ? 'Change Security PIN' : 'Setup Security PIN'}</h2>
            <p className="text-sm text-slate-400 mb-4">You must enter your current password to make this change.</p>
            <div className="space-y-4">
                <input type="password" value={authPassword} onChange={e => setAuthPassword(e.target.value)} placeholder="Current Password" className="w-full" />
                <hr className="border-slate-600"/>
                <input type="password" inputMode="numeric" maxLength={4} value={newPin} onChange={e => setNewPin(e.target.value)} placeholder="New 4-Digit PIN" className="w-full text-center tracking-[0.5em]"/>
                <input type="password" inputMode="numeric" maxLength={4} value={confirmNewPin} onChange={e => setConfirmNewPin(e.target.value)} placeholder="Confirm New PIN" className="w-full text-center tracking-[0.5em]"/>
            </div>
             <div className="mt-6 flex justify-end">
                <button onClick={handlePinSetup} disabled={isSettingPin} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg disabled:bg-slate-500">{isSettingPin ? 'Saving...' : 'Save PIN'}</button>
            </div>
        </>
    );

    const renderSetupOtp = () => (
         <>
            <button onClick={() => { clearMessages(); setActiveSection('main'); }} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-4"><ArrowLeftIcon className="w-4 h-4" /> Back to Profile</button>
            <h2 className="text-2xl font-bold text-white mb-4">{user?.otpEnabled ? 'Manage Authenticator App' : 'Setup Authenticator App'}</h2>
            
            {user?.otpEnabled ? (
                <div>
                    <p className="text-sm text-green-400 bg-green-900/50 border border-green-700 rounded-lg p-3 text-center mb-4">Authenticator app is currently enabled.</p>
                    <p className="text-sm text-slate-400 mb-4">To disable it, please enter your password.</p>
                    <input type="password" value={otpPassword} onChange={e => setOtpPassword(e.target.value)} placeholder="Current Password" className="w-full" />
                    <div className="mt-6 flex justify-end">
                        <button onClick={handleDisableOtp} disabled={isSettingOtp} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg disabled:bg-slate-500">{isSettingOtp ? 'Disabling...' : 'Disable'}</button>
                    </div>
                </div>
            ) : (
                <div className="text-center">
                    <p className="text-sm text-slate-300 mb-4">1. Scan the QR code with your authenticator app (e.g., Google Authenticator, Authy).</p>
                    {otpSetupData ? (
                        <div className="bg-white p-2 rounded-lg inline-block">
                            <img src={otpSetupData.qrCodeDataUrl} alt="OTP QR Code" />
                        </div>
                    ) : (
                         <div className="h-40 w-40 bg-slate-700 animate-pulse flex items-center justify-center rounded-lg mx-auto"><p className="text-slate-500 text-xs">Generating...</p></div>
                    )}
                     <p className="text-sm text-slate-400 mt-4">Or enter this key manually:</p>
                     <p className="font-mono bg-slate-900 text-amber-300 tracking-widest py-2 px-3 my-2 rounded-lg inline-block text-sm">{otpSetupData?.secret || '...'}</p>

                    <p className="text-sm text-slate-300 mt-6 mb-4">2. Enter your password and the 6-digit code from your app to verify.</p>
                    <div className="space-y-4 text-left">
                        <input type="password" value={otpPassword} onChange={e => setOtpPassword(e.target.value)} placeholder="Current Password" className="w-full" />
                        <input type="text" inputMode="numeric" maxLength={6} value={otpToken} onChange={e => setOtpToken(e.target.value)} placeholder="6-Digit Verification Code" className="w-full text-center tracking-[0.5em]"/>
                    </div>
                    <div className="mt-6 flex justify-end">
                        <button onClick={handleEnableOtp} disabled={isSettingOtp || !otpSetupData} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg disabled:bg-slate-500">{isSettingOtp ? 'Verifying...' : 'Verify & Enable'}</button>
                    </div>
                </div>
            )}
        </>
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
            <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md border border-slate-700 relative">
                <button onClick={handleClose} className="absolute top-3 right-3 text-slate-500 hover:text-white transition-colors">
                    <XCircleIcon className="w-8 h-8" />
                </button>
                
                {activeSection === 'main' && renderMain()}
                {activeSection === 'changePassword' && renderChangePassword()}
                {activeSection === 'setupPin' && renderSetupPin()}
                {activeSection === 'setupOtp' && renderSetupOtp()}

                {error && <p className="text-red-400 text-sm text-center mt-4">{error}</p>}
                {success && <p className="text-green-400 text-sm text-center mt-4">{success}</p>}
            </div>
        </div>
    );
};

export default ProfileModal;